import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Lock,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../lib/api";
import { toast } from "sonner";

export default function RecruiterSetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/recruiters/activate/${token}`);
        setInfo(res.data);
      } catch (e) {
        setError(
          e?.response?.data?.detail ||
            "This activation link is invalid or has expired."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6)
      return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/recruiters/activate/${token}`, {
        password,
      });
      localStorage.setItem("cct_recruiter_token", data.token);
      localStorage.setItem(
        "cct_recruiter",
        JSON.stringify(data.recruiter)
      );
      toast.success("Account activated — welcome aboard!");
      navigate("/recruiter/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Activation failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-cct-ambient flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cct-blue" />
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-cct-ambient">
        <Header minimal />
        <main className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="inline-flex w-16 h-16 rounded-full bg-red-50 items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="mt-4 font-display text-3xl font-semibold text-slate-900">
            Link Unavailable
          </h1>
          <p className="mt-2 text-slate-600">{error}</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1 text-sm text-cct-blue hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Back to portal
          </Link>
        </main>
      </div>
    );

  return (
    <div className="min-h-screen bg-cct-ambient">
      <Header minimal />
      <main className="max-w-md mx-auto px-4 pb-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-cct-blue mb-5"
        >
          <ArrowLeft className="w-4 h-4" /> Back to portal
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-sm p-7"
        >
          <div className="absolute -top-14 -right-14 w-48 h-48 rounded-full bg-[#C49A3A]/15 blur-3xl" />
          <div className="relative">
            <div className="inline-flex w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#2A4FAE] items-center justify-center shadow-sm">
              <KeyRound className="w-6 h-6 text-white" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-semibold text-slate-900">
              Welcome, {info?.recruiter?.full_name?.split(" ")[0]}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Set a password to activate your recruiter portal account.
            </p>

            <div className="mt-5 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Your User ID
              </div>
              <div className="font-medium text-slate-800 break-all">
                {info?.recruiter?.email}
              </div>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">
                  New Password
                </span>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="At least 6 characters"
                    className="w-full rounded-md border border-slate-300 bg-white pl-9 pr-9 py-2.5 text-sm input-focus"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cct-blue"
                  >
                    {show ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">
                  Confirm Password
                </span>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type={show ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2.5 text-sm input-focus"
                  />
                </div>
              </label>
              <button
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-cct-blue text-white font-semibold py-2.5 rounded-md hover:bg-[#1a3380] disabled:opacity-60"
              >
                {submitting ? "Activating..." : "Activate Account"}
                {!submitting && <CheckCircle2 className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
